import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { makeStyles, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    lineHeight: '1.6',
    fontSize: '14px',
    wordBreak: 'break-word',
    '& p': {
      margin: '0 0 8px',
      '&:last-child': {
        marginBottom: '0',
      },
    },
    '& ul, & ol': {
      margin: '0 0 8px',
      paddingLeft: '20px',
    },
    '& li': {
      margin: '2px 0',
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '12px 0 6px',
      fontWeight: 600,
    },
    '& a': {
      color: tokens.colorBrandForegroundLink,
    },
    '& table': {
      borderCollapse: 'collapse',
      margin: '0 0 8px',
    },
    '& th, & td': {
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      padding: '4px 8px',
      fontSize: '13px',
    },
    '& th': {
      fontWeight: 600,
    },
    '& hr': {
      border: 'none',
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
      margin: '12px 0',
    },
  },
  codeBlock: {
    display: 'block',
    overflowX: 'auto',
    padding: '10px 12px',
    margin: '8px 0',
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontFamily: '"Cascadia Mono", "Consolas", "Courier New", monospace',
    fontSize: '12.5px',
    lineHeight: '1.5',
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre',
  },
  inlineCode: {
    fontFamily: '"Cascadia Mono", "Consolas", "Courier New", monospace',
    fontSize: '0.9em',
    padding: '1px 4px',
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
});

interface MarkdownContentProps {
  content: string;
}

const MarkdownContent: React.FC<MarkdownContentProps> = ({ content }) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ className, children, ...props }) {
            const language = /language-(\w+)/.exec(className || '')?.[1];
            const text = String(children).replace(/\n$/, '');
            const isBlock = Boolean(language) || text.includes('\n');

            if (isBlock) {
              return (
                <code className={styles.codeBlock}>
                  {text}
                </code>
              );
            }

            return (
              <code className={styles.inlineCode} {...props}>
                {children}
              </code>
            );
          },
          pre({ children }) {
            return <>{children}</>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownContent;
